import Chapter from "./Chapter";
import { motion } from "framer-motion";
import { Weight, Footprints, Flame, Truck, ArrowDownToLine, Gamepad2, Ruler, Timer } from "lucide-react";

const FEATURES = [
  {
    icon: Weight,
    title: "Levage 13–45 cm · 3 000 kg",
    desc: "Soulève le véhicule par le châssis, sans cric ni bras de levage, avec une stabilité totale.",
    span: "lg:col-span-2",
    tag: "Puissance",
  },
  {
    icon: Footprints,
    title: "Mobilité sur chenilles",
    desc: "Franchit les sols difficiles et accède aux espaces les plus exigus.",
    span: "",
    tag: "Tout-terrain",
  },
  {
    icon: Flame,
    title: "Extinction des feux de véhicules",
    desc: "Projection d'eau ciblée, efficace sur les feux de batteries lithium (véhicules électriques).",
    span: "",
    tag: "Incendie",
  },
  {
    icon: Truck,
    title: "Déplacement de véhicules immobilisés",
    desc: "Évacue un véhicule en panne, mal garé ou inaccessible en quelques minutes, sans le démarrer.",
    span: "lg:col-span-2",
    tag: "Manutention",
  },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export default function Features() {
  return (
    <section id="produit" data-testid="features-section" className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <Chapter index={1} label="Un seul robot, deux missions" />
          <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Manutention de véhicules et lutte contre l'incendie.
          </h2>
          <p className="mt-4 text-lg text-[#52525B]">
            Sapeur-360 combine la force d'un engin de levage et l'agilité d'un robot à chenilles pour
            déplacer, extraire et sécuriser tout type de véhicule.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`feature-card-${i}`}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fade}
              custom={i}
              className={`group bg-white p-8 hover:bg-[#FAFAFA] transition-colors duration-300 ${f.span}`}
            >
              <div className="flex items-start justify-between">
                <span className="grid h-12 w-12 place-items-center bg-[#0A0A0A] text-white rounded-sm group-hover:bg-[#E63946] transition-colors duration-300">
                  <f.icon className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="font-mono-tech text-[11px] uppercase tracking-wider text-[#A1A1AA] border border-zinc-200 px-2 py-1 rounded-sm">
                  {f.tag}
                </span>
              </div>
              <h3 className="font-display mt-6 text-xl font-bold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-[#52525B] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Stats strip */}
        <div className="mt-px grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
          {[
            { icon: Weight, k: "3 000 kg", v: "Capacité de levage" },
            { icon: Ruler, k: "13–45 cm", v: "Amplitude de levage" },
            { icon: ArrowDownToLine, k: "Profil bas", v: "Se glisse sous le châssis" },
            { icon: Gamepad2, k: "À distance", v: "Pilotage sans risque" },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`} className="bg-white p-7">
              <s.icon className="h-5 w-5 text-[#E63946]" />
              <div className="font-display mt-3 text-3xl font-black tracking-tight">{s.k}</div>
              <div className="font-mono-tech text-xs uppercase tracking-wider text-[#A1A1AA] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
